// GameMonetize SDK adapter (Phase 1 — interstitial only).
//
// API surface (per github.com/MonetizeGame/GameMonetize.com-SDK README):
//   <script> sets window.SDK_OPTIONS = { gameId, onEvent } then injects
//   https://api.gamemonetize.com/sdk.js. Once loaded, the SDK exposes
//   `window.sdk.showBanner()` to play an interstitial. Events fire via
//   the onEvent callback: SDK_READY, SDK_GAME_PAUSE (ad about to play —
//   game must mute), SDK_GAME_START (ad finished — resume).
//
// The HTML script tag (injected by the Vite platform plugin) wires
// onEvent to dispatch into window.__gamemonetizeOnEvent so this adapter
// can subscribe. Events emitted before the adapter installs its handler
// are buffered in window.__gamemonetizeBuffer and replayed on init.
//
// Rewarded video is NOT in GM's public docs — rewardedBreak() returns
// false until they reply with the API surface (see project memory).
//
// Audio muting on SDK_GAME_PAUSE is the caller's responsibility: the
// game pauses around adapter.commercialBreak() the same way it does for
// CrazyGames / Poki, so audio is already silent before showBanner fires.

import type { PokiAdapter, ShareableURLParams } from '@/sdk/PokiAdapter';

const TAG = '[GameMonetizeSDK]';
const REAL_INIT_TIMEOUT_MS = 3000;
const POLL_INTERVAL_MS = 50;
// Safety net if no resume event fires (network drop, ad-blocker swallowed the
// ad request, etc.). Resolves with `false` so the AdGate fails closed — the
// caller decides whether that means "resume the game" (commercial) or "skip
// the reward" (rewarded). Every live impression today is a rewarded-button
// interstitial, so a stale resume here would mean a free reward without proof
// the ad played.
const AD_TIMEOUT_MS = 30000;

interface GameMonetizeSdk {
  showBanner: () => void;
}

interface GameMonetizeEvent {
  name: string;
}

declare global {
  interface Window {
    sdk?: GameMonetizeSdk;
    __gamemonetizeOnEvent?: (event: GameMonetizeEvent) => void;
    __gamemonetizeBuffer?: GameMonetizeEvent[];
  }
}

export class GameMonetizeAdapter implements PokiAdapter {
  private sdk: GameMonetizeSdk | null = null;
  private pendingAdResolver: (() => void) | null = null;
  private readonly timeoutMs: number;

  constructor(timeoutMs: number = REAL_INIT_TIMEOUT_MS) {
    this.timeoutMs = timeoutMs;
  }

  async init(): Promise<void> {
    const sdk = await waitForGameMonetizeSdk(this.timeoutMs);
    if (!sdk) {
      console.warn(`${TAG} window.sdk unavailable after ${this.timeoutMs}ms`);
      return;
    }
    this.sdk = sdk;
    if (typeof window === 'undefined') return;
    window.__gamemonetizeOnEvent = (event) => this.handleEvent(event);
    const buffered = window.__gamemonetizeBuffer ?? [];
    window.__gamemonetizeBuffer = [];
    for (const event of buffered) this.handleEvent(event);
  }

  // The GM SDK is a GameDistribution/IMA derivative — despite the README
  // describing SDK_GAME_START as "advertisement done", that event fires
  // before the ad video has finished playing. The actual "ad UI is gone,
  // resume your game" signal is CONTENT_RESUME_REQUESTED (IAB VPAID
  // naming). AD_ERROR / AD_CANCELLED cover failure/dismissal paths.
  private static readonly AD_DONE_EVENTS: ReadonlySet<string> = new Set([
    'CONTENT_RESUME_REQUESTED',
    'AD_ERROR',
    // Live GM SDK spells this with one L (US English); accept both in case
    // the SDK version varies.
    'AD_CANCELED',
    'AD_CANCELLED',
    'AD_SDK_FINISHED',
  ]);

  private handleEvent(event: GameMonetizeEvent): void {
    if (GameMonetizeAdapter.AD_DONE_EVENTS.has(event.name) && this.pendingAdResolver) {
      const resolver = this.pendingAdResolver;
      this.pendingAdResolver = null;
      resolver();
    }
  }

  gameLoadingStart(): void {}

  gameLoadingFinished(): void {}

  // GM has no explicit gameplay-start signal — ads are fired on demand.
  gameplayStart(): void {}

  gameplayStop(): void {}

  async commercialBreak(): Promise<void> {
    await this.playAd();
  }

  // GM's public docs only expose sdk.showBanner() (interstitial). Until they
  // publish a rewarded API, this gate plays an interstitial and grants the
  // reward on completion — user clicks "watch ad", ad plays, reward granted.
  // Returns false only when the SDK isn't loaded or showBanner throws (no ad
  // shown = no reward). When GM exposes SDK_REWARDED_WATCH_COMPLETE or
  // similar, swap the implementation inside playAd().
  rewardedBreak(): Promise<boolean> {
    return this.playAd();
  }

  private playAd(): Promise<boolean> {
    if (!this.sdk) return Promise.resolve(false);
    return new Promise<boolean>((resolve) => {
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const settle = (success: boolean) => {
        if (settled) return;
        settled = true;
        if (timer !== null) clearTimeout(timer);
        this.pendingAdResolver = null;
        resolve(success);
      };
      this.pendingAdResolver = () => settle(true);
      timer = setTimeout(() => settle(false), AD_TIMEOUT_MS);
      try {
        this.sdk?.showBanner();
      } catch (error) {
        console.warn(`${TAG} showBanner threw`, error);
        settle(false);
      }
    });
  }

  happyTime(_intensity: number): void {}

  captureError(error: unknown, context?: string): void {
    console.error(`${TAG} captureError`, context, error);
  }

  async shareableURL(params: ShareableURLParams): Promise<string> {
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    url.search = '';
    for (const [k, v] of Object.entries(params)) {
      url.searchParams.set(k, String(v));
    }
    return url.toString();
  }
}

async function waitForGameMonetizeSdk(timeoutMs: number): Promise<GameMonetizeSdk | null> {
  if (typeof window === 'undefined') return null;
  if (window.sdk) return window.sdk;
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    if (window.sdk) return window.sdk;
  }
  return null;
}
